import { Button, type ButtonProps } from "@dropbox/dig-components/buttons";
import { Menu } from "@dropbox/dig-components/menu";
import { useAvailableModels } from "@mirage/service-conversation/hooks/useAvailableModels";
import i18n from "@mirage/translations";
import { memo, useMemo } from "react";

import { PAP_Click_DashChatAction } from "@mirage/analytics/events/types/click_dash_chat_action";
import type { ModelId } from "@mirage/service-conversation/types";

import { PAPEvent } from "@mirage/analytics/events/base/event";
import { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";

interface ModelPickerProps {
  selectedModel: ModelId;
  setSelectedModel: (model: ModelId) => void;
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  dataTestId?: string;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: { actionSurfaceComponent?: ActionSurfaceComponent },
  ) => void;
  conversationId?: string;
}

export const ModelPicker = memo(
  ({
    selectedModel,
    setSelectedModel,
    dataTestId = "model-selector",
    variant = "filled",
    size = "medium",
    logComposeEvent,
    conversationId,
  }: ModelPickerProps) => {
    const { availableModels } = useAvailableModels();
    const { certifiedModelOptions, experimentalModelOptions } = useMemo(() => {
      const defaultModel = {
        value: "",
        label: i18n.t("model_picker_default_model_label"),
        isSelected: !selectedModel,
      };

      const certifiedModels = availableModels.models
        .filter((model) => model.isCertified)
        .map((model) => ({
          value: model.id,
          label: model.displayName,
          isSelected: selectedModel === model.id,
        }));

      const experimentalModels = availableModels.models
        .filter((model) => !model.isCertified)
        .map((model) => ({
          value: model.id,
          label: model.displayName,
          isSelected: selectedModel === model.id,
        }));

      return {
        certifiedModelOptions: [defaultModel, ...certifiedModels],
        experimentalModelOptions: experimentalModels,
      };
    }, [selectedModel, availableModels]);

    return (
      <Menu.Wrapper
        onSelection={(selection: ModelId) => {
          logComposeEvent(
            PAP_Click_DashChatAction({
              featureLine: "assist",
              dashChatAction: "change_ai_model",
              conversationId,
            }),
          );
          setSelectedModel(selection);
        }}
      >
        {({ getContentProps, getTriggerProps }) => (
          <>
            <Button
              {...getTriggerProps()}
              variant={variant}
              size={size}
              data-testid={dataTestId}
              withDropdownIcon
            >
              {selectedModel || i18n.t("model_picker_button_label")}
            </Button>
            <Menu.Content {...getContentProps()} auto>
              <Menu.Segment>
                {certifiedModelOptions.map((option) => (
                  <Menu.SelectItem
                    key={option.value}
                    value={option.value}
                    selected={option.isSelected}
                  >
                    {option.label}
                  </Menu.SelectItem>
                ))}
              </Menu.Segment>
              <Menu.Segment
                withLabel={i18n.t("model_picker_experimental_models_label")}
              >
                {experimentalModelOptions.length > 0 && (
                  <Menu.Submenu
                    withTriggerContent={i18n.t(
                      "model_picker_more_models_label",
                      { count: experimentalModelOptions.length },
                    )}
                  >
                    <Menu.Segment
                      withLabel={i18n.t(
                        "model_picker_experimental_models_label",
                      )}
                    >
                      {experimentalModelOptions.map((option) => (
                        <Menu.SelectItem
                          key={option.value}
                          value={option.value}
                          selected={option.isSelected}
                        >
                          {option.label}
                        </Menu.SelectItem>
                      ))}
                    </Menu.Segment>
                  </Menu.Submenu>
                )}
              </Menu.Segment>
            </Menu.Content>
          </>
        )}
      </Menu.Wrapper>
    );
  },
);

ModelPicker.displayName = "ModelPicker";
